import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Field, SummaryStat } from "@/pages/Income";
import { Plus, Pencil, Trash2 } from "lucide-react";
import DateField from "@/components/DateField";

export default function Loans() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name:"", lender:"", principal:"", outstanding:"", interest_rate:"", emi:"", tenure_months:"", remaining_months:"", start_date:new Date().toISOString().slice(0,10) });

  const load = useCallback(async () => { const { data } = await api.get("/loans"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEdit(null); setForm({ name:"", lender:"", principal:"", outstanding:"", interest_rate:"", emi:"", tenure_months:"", remaining_months:"", start_date:new Date().toISOString().slice(0,10) }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ ...i }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, lender: form.lender,
      principal: parseFloat(form.principal), outstanding: parseFloat(form.outstanding),
      interest_rate: parseFloat(form.interest_rate), emi: parseFloat(form.emi),
      tenure_months: parseInt(form.tenure_months), remaining_months: parseInt(form.remaining_months),
      start_date: form.start_date,
    };
    try {
      if (edit) await api.put(`/loans/${edit.id}`, payload); else await api.post("/loans", payload);
      toast.success(edit ? "Loan updated" : "Loan added");
      setOpen(false); load();
    } catch { toast.error(t("common.toast_failed")); }
  };
  const remove = async (id) => { if (!confirm("Delete loan?")) return; await api.delete(`/loans/${id}`); toast.success(t("common.toast_deleted")); load(); };

  const cur = user?.currency || "USD";
  const totalOut = items.reduce((a,b) => a+Number(b.outstanding||0), 0);
  const totalEmi = items.reduce((a,b) => a+Number(b.emi||0), 0);
  const wAvgRate = totalOut > 0 ? items.reduce((a,b) => a + b.interest_rate * b.outstanding, 0) / totalOut : 0;

  return (
    <div>
      <PageHeader
        subtitle={t("pages.loans.subtitle")}
        title={t("pages.loans.title")}
        action={<button className="btn-primary" onClick={startNew} data-testid="add-loan-btn"><Plus size={16}/> {t("pages.loans.add")}</button>}
      />

      <div className="grid md:grid-cols-4 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.loans_body.active_loans")} value={items.length}/>
        <SummaryStat label={t("pages.loans_body.total_outstanding")} value={formatMoney(totalOut, cur)}/>
        <SummaryStat label={t("pages.loans_body.monthly_emi")} value={formatMoney(totalEmi, cur)}/>
        <SummaryStat label={t("pages.loans_body.weighted_apr")} value={`${wAvgRate.toFixed(2)}%`}/>
      </div>

      {items.length === 0 ? (
        <EmptyState title={t("pages.loans.empty_title")} body={t("pages.loans_body.empty_body")}
          action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.loans.add")}</button>}/>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {items.map(l => {
            const progress = Math.max(0, Math.min(100, 100 * (1 - (l.remaining_months / (l.tenure_months || 1)))));
            const paid = l.principal - l.outstanding;
            return (
              <div key={l.id} className="card-flat p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-medium" style={{fontFamily:'var(--font-heading)'}}>{l.name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{l.lender || "—"}</div>
                  </div>
                  <div className="text-right">
                    <span className={`badge ${l.interest_rate >= 12 ? "badge-danger" : l.interest_rate >= 8 ? "badge-warn" : "badge-success"}`}>{l.interest_rate}% {t("pages.loans_body.apr_suffix")}</span>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div><div className="label-eyebrow">{t("pages.loans_body.outstanding")}</div><div className="num-md mt-1">{formatMoney(l.outstanding, cur)}</div></div>
                  <div><div className="label-eyebrow">{t("pages.loans_body.monthly_emi")}</div><div className="num-md mt-1">{formatMoney(l.emi, cur)}</div></div>
                  <div><div className="label-eyebrow">{t("pages.loans_body.remaining")}</div><div className="num-md mt-1">{l.remaining_months} / {l.tenure_months} {t("pages.loans_body.months_short")}</div></div>
                  <div><div className="label-eyebrow">{t("pages.loans_body.paid_off")}</div><div className="num-md mt-1 text-[var(--accent-positive)]">{formatMoney(paid, cur)}</div></div>
                </div>
                <div className="mt-5">
                  <div className="h-2 rounded-full bg-[var(--bg-accent)] overflow-hidden">
                    <div className="h-full bg-[var(--accent-primary)]" style={{ width: `${progress}%` }}/>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1.5">{t("pages.loans_body.paid_off_pct", { pct: progress.toFixed(1) })}</div>
                </div>
                <div className="mt-5 flex justify-end gap-1">
                  <button className="btn-danger-ghost" onClick={() => startEdit(l)}><Pencil size={15}/></button>
                  <button className="btn-danger-ghost" onClick={() => remove(l.id)}><Trash2 size={15}/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("pages.loans_body.edit_title") : t("pages.loans_body.add_title")} size="lg">
        <form onSubmit={save} className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.loans_body.loan_name")}><input required className="field-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="input-loan-name"/></Field>
            <Field label={t("pages.loans_body.lender")}><input className="field-input" value={form.lender} onChange={e => setForm({...form, lender: e.target.value})} data-testid="input-loan-lender"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.loans_body.principal", { cur })}><input type="number" step="0.01" required className="field-input" value={form.principal} onChange={e => setForm({...form, principal: e.target.value})} data-testid="input-loan-principal"/></Field>
            <Field label={t("pages.loans_body.outstanding_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.outstanding} onChange={e => setForm({...form, outstanding: e.target.value})} data-testid="input-loan-outstanding"/></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label={t("pages.loans_body.interest_rate")}><input type="number" step="0.01" required className="field-input" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} data-testid="input-loan-rate"/></Field>
            <Field label={t("pages.loans_body.emi_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.emi} onChange={e => setForm({...form, emi: e.target.value})} data-testid="input-loan-emi"/></Field>
            <Field label={t("pages.loans_body.start_date")}><DateField value={form.start_date} onChange={v => setForm({...form, start_date: v})} testid="input-loan-date"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.loans_body.tenure_months")}><input type="number" required className="field-input" value={form.tenure_months} onChange={e => setForm({...form, tenure_months: e.target.value})} data-testid="input-loan-tenure"/></Field>
            <Field label={t("pages.loans_body.remaining_months")}><input type="number" required className="field-input" value={form.remaining_months} onChange={e => setForm({...form, remaining_months: e.target.value})} data-testid="input-loan-remaining"/></Field>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-loan-btn">{edit ? t("common.save") : t("pages.loans_body.save_cta")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
